import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import {
  CloudSync,
  Lock,
  OpenInNew,
  PlayArrow,
  Refresh,
  Search,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService, type ShopifyMapAuditFinding, type ShopifyMapAuditIssue } from '../services/api';
import { integrationApiService } from '../services/integrationApi';
import {
  auditResyncTargetKey,
  isAuditFindingResyncable,
  uniqueAuditResyncTargets,
} from '../utils/shopifyAuditResync';
import { deriveStorePlatform } from '../utils/storePlatform';
import { ConfirmDialog, EmptyState, LoadingState, PageContainer, PageHeader, StatusChip, StatusTone } from '../components/ui';
import { useIsMobile } from '../hooks/useIsMobile';
import { tokens } from '../theme/tokens';
import { useLocale } from '../contexts/LocaleContext';
import { useAppTranslation } from '../i18n/useAppTranslation';

const mobileLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '.06em',
  color: tokens.ink.faint,
  display: 'block',
};

const ISSUE_LABEL: Record<ShopifyMapAuditIssue, string> = {
  below_map: 'Below MAP',
  suspicious_discount: 'Suspicious discount',
  stale_price: 'Stale price',
  stale_compare_at: 'Stale compare-at',
  missing_live_price: 'Missing live price',
  unmapped_variant: 'Unmapped variant',
};

const ISSUE_TONE: Record<ShopifyMapAuditIssue, StatusTone> = {
  below_map: 'problem',
  suspicious_discount: 'problem',
  stale_price: 'caution',
  stale_compare_at: 'caution',
  missing_live_price: 'caution',
  unmapped_variant: 'caution',
};

const money = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtWhen = (value: unknown): string => {
  if (!value) return 'Never';
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime()) || d.getTime() === 0) return 'Never';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const StoreShopifyPricingAuditPage: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { formatNumber } = useLocale();
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const integrationId = searchParams.get('integrationId') || '';
  const highlightedProductId = searchParams.get('productId') || '';
  const [issueFilter, setIssueFilter] = useState<ShopifyMapAuditIssue | 'all'>('all');
  const [search, setSearch] = useState(highlightedProductId);

  const integrationsQuery = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationApiService.getIntegrations(),
  });

  const shopifyIntegrations = useMemo(
    () => (integrationsQuery.data || []).filter((row) => row.platform === 'shopify'),
    [integrationsQuery.data]
  );
  const storePlatform = useMemo(() => deriveStorePlatform(integrationsQuery.data), [integrationsQuery.data]);

  const auditQuery = useQuery({
    queryKey: ['shopify-pricing-audit', integrationId || 'all'],
    queryFn: () => apiService.getShopifyPricingAudit({
      integrationId: integrationId || undefined,
    }),
    // Nothing to audit until the store has a Shopify integration.
    enabled: shopifyIntegrations.length > 0,
    refetchInterval: (query) => (
      query.state.data?.summary?.status === 'running' ? 4000 : false
    ),
  });

  const runMutation = useMutation({
    mutationFn: () => apiService.runShopifyPricingAudit(integrationId || undefined),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['shopify-pricing-audit'] });
    },
  });
  const [pushedKeys, setPushedKeys] = useState<Set<string>>(new Set());
  const [pushingKey, setPushingKey] = useState<string | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [pushNotice, setPushNotice] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);

  const summary = auditQuery.data?.summary;
  const auditRunning = summary?.status === 'running';
  const canResume =
    summary?.status === 'error' &&
    (
      summary.phase === 'unmapped' ||
      Boolean(summary.mappingsTotal && (summary.mappingsOffset || summary.mappingsChecked || 0) < summary.mappingsTotal) ||
      (summary.variantsChecked || 0) > 0
    );
  const runBusy = runMutation.isPending || auditRunning;
  const progressLabel = summary?.mappingsTotal
    ? `${formatNumber(summary.mappingsChecked || 0)} of ${formatNumber(summary.mappingsTotal)} products / ${formatNumber(summary.variantsChecked || 0)} variants`
    : `${formatNumber(summary?.mappingsChecked || 0)} products / ${formatNumber(summary?.variantsChecked || 0)} variants`;
  const findings = auditQuery.data?.findings || [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return findings.filter((row) => {
      if (issueFilter !== 'all' && !row.issues.includes(issueFilter)) return false;
      if (!q) return true;
      return [
        row.productName,
        row.productSku,
        row.productBrand,
        row.productModel,
        row.productId,
        row.shopifySku,
      ].some((value) => String(value || '').toLowerCase().includes(q));
    });
  }, [findings, issueFilter, search]);
  const visibleFindings = useMemo(
    () => filtered.filter((row) => !pushedKeys.has(auditResyncTargetKey(row))),
    [filtered, pushedKeys],
  );
  const resyncTargets = useMemo(() => uniqueAuditResyncTargets(visibleFindings), [visibleFindings]);
  // One representative finding per target, so the confirm dialog can name each listing
  // and show the price change it is about to push.
  const resyncPreview = useMemo(() => {
    const byKey = new Map<string, ShopifyMapAuditFinding>();
    for (const row of visibleFindings) {
      const key = auditResyncTargetKey(row);
      if (!byKey.has(key)) byKey.set(key, row);
    }
    return resyncTargets
      .map((target) => byKey.get(target.key))
      .filter((row): row is ShopifyMapAuditFinding => row != null);
  }, [visibleFindings, resyncTargets]);
  // Nothing to show: no audit has produced data yet. One empty state replaces the
  // zero stat cards and the stacked banners.
  const hasAuditData =
    Boolean(summary?.auditRunId) || (summary?.variantsChecked || 0) > 0 || findings.length > 0;
  const showEmptyOnly =
    !auditQuery.isLoading && !integrationsQuery.isLoading && !auditRunning && !hasAuditData && !summary?.error;
  const noShopify = shopifyIntegrations.length === 0;
  const auditStoreLabel = (() => {
    const domain = summary?.shopDomain;
    if (!domain) return '';
    const match = shopifyIntegrations.find((row) => row.credentials?.shopDomain === domain);
    return match?.name || domain.replace(/\.myshopify\.com$/i, '');
  })();
  const pushBusy = pushingKey != null || bulkProgress != null;

  const pushTarget = async (target: { productId: string; integrationId: string; key: string }) => {
    const payload = await apiService.forceErpResync(target.productId, target.integrationId);
    const failed = (payload.results || []).filter((row) => row.status === 'error');
    if (failed.length === (payload.results || []).length && (payload.results || []).length > 0) {
      throw new Error(failed[0]?.error || 'Shopify rejected the price push');
    }
    setPushedKeys((prev) => new Set(prev).add(target.key));
    return payload;
  };

  const runBulkFix = () => {
    setPushNotice(null);
    setBulkProgress({ done: 0, total: resyncTargets.length });
    void apiService.resyncShopifyAuditFindings({
      integrationId: integrationId || undefined,
      targets: resyncTargets.map(({ productId, integrationId: id }) => ({
        productId,
        integrationId: id,
      })),
    })
      .then((result) => {
        if (result.failed === 0 && result.pushed > 0) {
          setPushedKeys((prev) => {
            const next = new Set(prev);
            for (const row of resyncTargets) next.add(row.key);
            return next;
          });
        }
        if (result.failed === 0 && result.attempted > 0) {
          setPushNotice({
            tone: 'success',
            text: `Pushed Motzu price and compare-at for ${result.pushed} listing${result.pushed === 1 ? '' : 's'}. Run the audit again to confirm Shopify matches.`,
          });
        } else if (result.attempted === 0) {
          setPushNotice({
            tone: 'info',
            text: 'No stale price or compare-at listings to push on this audit.',
          });
        } else {
          setPushNotice({
            tone: 'error',
            text: `Pushed ${result.pushed}, failed ${result.failed}. ${result.errors.slice(0, 3).join(' · ')}`,
          });
        }
      })
      .catch((err) => {
        setPushNotice({
          tone: 'error',
          text: (err as Error).message || 'Bulk Shopify push failed',
        });
      })
      .finally(() => setBulkProgress(null));
  };

  const stat = (label: string, value: number, tone: 'error' | 'warning' | 'default' = 'default') => (
    <Paper variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 160, flex: '1 1 160px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: tone === 'error' ? 'error.main' : tone === 'warning' ? 'warning.main' : 'text.primary',
        }}
      >
        {formatNumber(value)}
      </Typography>
    </Paper>
  );

  // The audit reads live Shopify listings, so it only exists for stores with a Shopify
  // integration. Everyone else gets a plain explanation instead of an empty audit.
  if (integrationsQuery.isLoading) {
    return (
      <PageContainer maxWidth={1400}>
        <LoadingState size="page" />
      </PageContainer>
    );
  }
  if (integrationsQuery.isError) {
    return (
      <PageContainer maxWidth={1400}>
        <PageHeader title="Shopify pricing audit" />
        <Alert severity="error" sx={{ mt: 2 }}>
          Could not load your integrations. Refresh the page to try again.
        </Alert>
      </PageContainer>
    );
  }
  if (shopifyIntegrations.length === 0) {
    return (
      <PageContainer maxWidth={1400}>
        <PageHeader
          title="Shopify pricing audit"
          description="Compare live Shopify listing prices to Motzu MAP and the price Motzu would push."
        />
        <EmptyState
          title="Only available for Shopify stores"
          description={
            storePlatform.hasIntegration
              ? `Your store is connected to ${storePlatform.platformsLabel}. This audit reads live Shopify listings, so it is not available for that platform. Use Pricing & MAP data to review your prices.`
              : 'This audit reads live Shopify listings. Connect a Shopify integration on the Integrations page to use it.'
          }
          actionLabel={storePlatform.hasIntegration ? 'Open Pricing & MAP data' : 'Go to Integrations'}
          onAction={() => navigate(storePlatform.hasIntegration ? '/app/map-pricing-data' : '/app/integrations')}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth={1400}>
      <PageHeader
        title="Shopify Pricing Audit"
        description="Check your live Shopify prices against Motzu MAP and fix stale ones."
        actions={
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => void auditQuery.refetch()}
            disabled={auditQuery.isFetching}
          >
            Refresh
          </Button>
          {resyncTargets.length > 0 && (
            <Button
              variant="contained"
              color="warning"
              startIcon={pushBusy ? <CircularProgress size={16} color="inherit" /> : <CloudSync />}
              disabled={pushBusy || auditRunning}
              onClick={() => setBulkConfirmOpen(true)}
            >
              {bulkProgress
                ? `Fixing ${bulkProgress.total} listings…`
                : `Fix all stale listings (${resyncTargets.length})`}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={runBusy ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
            onClick={() => runMutation.mutate()}
            disabled={runBusy || shopifyIntegrations.length === 0}
          >
            {auditRunning ? 'Audit running' : canResume ? 'Continue audit' : 'Run audit'}
          </Button>
        </Stack>
        }
      />

      {showEmptyOnly && (
        <Box sx={{ mt: 3 }}>
          {noShopify ? (
            <EmptyState
              title="Connect Shopify to run an audit"
              description="Once a Shopify store is connected, the audit compares its live prices to Motzu MAP."
              actionLabel="Go to integrations"
              onAction={() => navigate('/app/integrations')}
            />
          ) : (
            <EmptyState
              title="No audit yet"
              description="Run an audit to compare your live Shopify prices with Motzu MAP."
              actionLabel="Run audit"
              onAction={() => runMutation.mutate()}
            />
          )}
        </Box>
      )}

      {pushNotice && (
        <Alert severity={pushNotice.tone} sx={{ mt: 2 }} onClose={() => setPushNotice(null)}>
          {pushNotice.text}
        </Alert>
      )}

      {auditRunning && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Audit is running. Live Shopify prices are being compared to Motzu — this page
          refreshes on its own. A gateway timeout on the first click does not stop the job.
        </Alert>
      )}

      {/*
        Shown regardless of any error on the loaded summary. That error describes a
        previous run; using it to suppress this one hid live auth and network
        failures behind a stale warning. Queued and in-flight responses no longer
        reach here as errors — `runShopifyPricingAudit` resolves those — so an
        error at this point is a real failure of this request.
      */}
      {runMutation.isError && !auditRunning && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {(runMutation.error as Error)?.message || 'Audit failed'}
        </Alert>
      )}
      {summary?.error && summary.status !== 'running' && (
        <Alert severity="warning" sx={{ mt: 2 }}>{summary.error}</Alert>
      )}

      {/*
        Free Preview cap (flag map_audit_ladder): the run checked the first
        `mapAuditCap` mapped products. The server sends the two fields only
        when a cap applied, so with the flag off (or on a paid plan) this
        never renders.
      */}
      {!auditRunning && summary?.mapAuditCap != null && (summary.productsNotAuditedByPlan || 0) > 0 && (
        <Alert
          severity="info"
          icon={<Lock fontSize="small" />}
          action={
            <Button size="small" onClick={() => navigate('/app/billing')} sx={{ whiteSpace: 'nowrap' }}>
              {t('store.shopifyPricingAudit.capNoticeCta')}
            </Button>
          }
          sx={{ mt: 2 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t('store.shopifyPricingAudit.capNoticeTitle', { count: summary.productsNotAuditedByPlan || 0 })}
          </Typography>
          <Typography variant="body2">
            {t('store.shopifyPricingAudit.capNoticeBody', { cap: formatNumber(summary.mapAuditCap) })}
          </Typography>
        </Alert>
      )}

      {!showEmptyOnly && (<>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }} flexWrap="wrap">
        {stat('Below MAP', summary?.belowMap || 0, 'error')}
        {stat('Suspicious', summary?.suspiciousDiscount || 0, 'error')}
        {stat('Stale price', summary?.stalePrice || 0, 'warning')}
        {stat('Stale compare-at', summary?.staleCompareAt || 0, 'warning')}
        {stat('Unmapped variants', summary?.unmappedVariant || 0, 'warning')}
        {stat('Variants checked', summary?.variantsChecked || 0)}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Price issues are listings Motzu could compare. Unmapped variants are Shopify listings with no matching Motzu product, so
        there is no price to check; they are counted separately and are not part of the dashboard's price mismatches or the SKU crosswalk.
      </Typography>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        {auditRunning ? 'Started' : 'Last audit'}: {fmtWhen(summary?.completedAt || summary?.startedAt)}
        {auditStoreLabel ? ` · ${auditStoreLabel}` : ''}
        {summary?.auditRunId && (auditRunning || summary.status === 'error')
          ? ` · ${progressLabel} checked so far`
          : ''}
        {!auditRunning && summary?.newViolations
          ? ` · ${summary.newViolations} new MAP violation${summary.newViolations === 1 ? '' : 's'}`
          : ''}
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
        <TextField
          size="small"
          placeholder="Search product, SKU, or brand"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} /> }}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Issue</InputLabel>
          <Select
            label="Issue"
            value={issueFilter}
            onChange={(e) => setIssueFilter(e.target.value as ShopifyMapAuditIssue | 'all')}
          >
            <MenuItem value="all">All issues</MenuItem>
            <MenuItem value="below_map">Below MAP</MenuItem>
            <MenuItem value="suspicious_discount">Suspicious discount</MenuItem>
            <MenuItem value="stale_price">Stale price</MenuItem>
            <MenuItem value="stale_compare_at">Stale compare-at</MenuItem>
            <MenuItem value="missing_live_price">Missing live price</MenuItem>
            <MenuItem value="unmapped_variant">Unmapped variant</MenuItem>
          </Select>
        </FormControl>
        {shopifyIntegrations.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Shopify store</InputLabel>
            <Select
              label="Shopify store"
              value={integrationId}
              onChange={(e) => {
                const next = new URLSearchParams(searchParams);
                if (e.target.value) next.set('integrationId', String(e.target.value));
                else next.delete('integrationId');
                setSearchParams(next);
              }}
            >
              <MenuItem value="">All Shopify stores</MenuItem>
              {shopifyIntegrations.map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {row.name || row.credentials?.shopDomain || row.credentials?.shopId || row.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>

      {auditQuery.isLoading ? (
        <LoadingState size="page" />
      ) : auditRunning && visibleFindings.length === 0 ? (
        <Alert severity="info" sx={{ mt: 3 }}>
          Still walking live Shopify listings. Counts update as each batch finishes —
          zeros here mean the job has not checked a product yet, or the last run died
          after it marked itself running.
        </Alert>
      ) : visibleFindings.length === 0 ? (
        <Alert
          severity={findings.length === 0 && summary?.status === 'complete' && summary.auditRunId ? 'success' : 'info'}
          sx={{ mt: 3 }}
        >
          {findings.length === 0
            ? summary?.status === 'complete' && summary.auditRunId
              ? 'All checked Shopify variants match Motzu MAP and intended prices.'
              : summary?.status === 'error' && (summary.variantsChecked || 0) > 0
                ? 'Audit stopped partway. Continue it — it will pick up where it left off, and issues appear as each batch finishes.'
                : 'No Shopify pricing issues yet. Run an audit to compare live listings with Motzu.'
            : pushedKeys.size > 0 && filtered.length > 0 && visibleFindings.length === 0
              ? 'Pushed Motzu prices for the matching listings. Run the audit again to confirm Shopify matches.'
              : 'No findings match this filter.'}
        </Alert>
      ) : isMobile ? (
        <Paper variant="outlined" sx={{ mt: 3, borderRadius: 2 }}>
          <Stack divider={<Divider />}>
            {visibleFindings.map((row) => (
              <FindingCard
                key={row.id}
                row={row}
                highlighted={row.productId === highlightedProductId}
                resyncPending={pushingKey === auditResyncTargetKey(row)}
                resyncDisabled={pushBusy || auditRunning}
                onResync={() => {
                  const target = uniqueAuditResyncTargets([row])[0];
                  if (!target) return;
                  setPushNotice(null);
                  setPushingKey(target.key);
                  void pushTarget(target)
                    .then(() => {
                      setPushNotice({
                        tone: 'success',
                        text: `Pushed Motzu price ${money(row.intendedPrice)} and compare-at ${money(row.intendedCompareAtPrice)} to Shopify.`,
                      });
                    })
                    .catch((err) => {
                      setPushNotice({
                        tone: 'error',
                        text: (err as Error).message || 'Shopify resync failed',
                      });
                    })
                    .finally(() => setPushingKey(null));
                }}
                onOpenProduct={() => navigate(`/app/products?productId=${encodeURIComponent(row.productId)}`)}
              />
            ))}
          </Stack>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Issues</TableCell>
                <TableCell align="right">Shopify price</TableCell>
                <TableCell align="right">Motzu price</TableCell>
                <TableCell align="right">MAP floor</TableCell>
                <TableCell align="right">Compare-at</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleFindings.map((row) => (
                <FindingRow
                  key={row.id}
                  row={row}
                  highlighted={row.productId === highlightedProductId}
                  resyncPending={pushingKey === auditResyncTargetKey(row)}
                  resyncDisabled={pushBusy || auditRunning}
                  onResync={() => {
                    const target = uniqueAuditResyncTargets([row])[0];
                    if (!target) return;
                    setPushNotice(null);
                    setPushingKey(target.key);
                    void pushTarget(target)
                      .then(() => {
                        setPushNotice({
                          tone: 'success',
                          text: `Pushed Motzu price ${money(row.intendedPrice)} and compare-at ${money(row.intendedCompareAtPrice)} to Shopify.`,
                        });
                      })
                      .catch((err) => {
                        setPushNotice({
                          tone: 'error',
                          text: (err as Error).message || 'Shopify resync failed',
                        });
                      })
                      .finally(() => setPushingKey(null));
                  }}
                  onOpenProduct={() => navigate(`/app/products?productId=${encodeURIComponent(row.productId)}`)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </>)}

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Update ${resyncTargets.length} listing${resyncTargets.length === 1 ? '' : 's'} on Shopify?`}
        confirmLabel={`Update ${resyncTargets.length} listing${resyncTargets.length === 1 ? '' : 's'}`}
        confirmColor="warning"
        message={
          <Box>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              This pushes the Motzu price and compare-at to {resyncTargets.length} listing
              {resyncTargets.length === 1 ? '' : 's'} on your live Shopify store. Customers will see
              the new prices right away.
            </Typography>
            <Box
              sx={{
                maxHeight: 240,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              {resyncPreview.map((row) => (
                <Box
                  key={auditResyncTargetKey(row)}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {row.productName || row.productId}
                    </Typography>
                    {(row.productSku || row.shopifySku) && (
                      <Typography variant="caption" color="text.secondary">
                        {row.productSku || row.shopifySku}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {row.intendedPrice != null
                      ? `${money(row.livePrice)} → ${money(row.intendedPrice)}`
                      : `Compare-at ${money(row.liveCompareAtPrice)} → ${money(row.intendedCompareAtPrice)}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        }
        onConfirm={() => {
          setBulkConfirmOpen(false);
          runBulkFix();
        }}
        onClose={() => setBulkConfirmOpen(false)}
      />
    </PageContainer>
  );
};

const FindingRow: React.FC<{
  row: ShopifyMapAuditFinding;
  highlighted: boolean;
  resyncPending: boolean;
  resyncDisabled: boolean;
  onResync: () => void;
  onOpenProduct: () => void;
}> = ({ row, highlighted, resyncPending, resyncDisabled, onResync, onOpenProduct }) => (
  <TableRow
    sx={{
      bgcolor: highlighted ? (theme) => alpha(theme.palette.warning.main, 0.08) : undefined,
    }}
  >
    <TableCell>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {row.productName || row.productId}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {[row.productBrand, row.productSku || row.shopifySku].filter(Boolean).join(' · ')}
        {row.hostProductId ? ' · sibling variant on another Motzu SKU\'s listing' : ''}
      </Typography>
    </TableCell>
    <TableCell>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {row.issues.map((issue) => (
          <StatusChip key={issue} size="small" label={ISSUE_LABEL[issue]} tone={ISSUE_TONE[issue]} />
        ))}
      </Stack>
    </TableCell>
    <TableCell align="right">{money(row.livePrice)}</TableCell>
    <TableCell align="right">{money(row.intendedPrice)}</TableCell>
    <TableCell align="right">{money(row.mapFloor)}</TableCell>
    <TableCell align="right">
      <Typography variant="body2">{money(row.liveCompareAtPrice)}</Typography>
      <Typography variant="caption" color="text.secondary">
        Motzu {money(row.intendedCompareAtPrice)}
      </Typography>
    </TableCell>
    <TableCell align="right">
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        {isAuditFindingResyncable(row.issues) && (row.intendedPrice != null || row.intendedCompareAtPrice != null) && (
          <Button
            size="small"
            onClick={onResync}
            disabled={resyncDisabled}
            startIcon={resyncPending ? <CircularProgress size={12} /> : <CloudSync sx={{ fontSize: 14 }} />}
            sx={{ textTransform: 'none' }}
          >
            Resync
          </Button>
        )}
        <Button size="small" onClick={onOpenProduct} sx={{ textTransform: 'none' }}>
          Motzu
        </Button>
        {row.shopifyAdminUrl && (
          <Button
            size="small"
            href={row.shopifyAdminUrl}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
            sx={{ textTransform: 'none' }}
          >
            Shopify
          </Button>
        )}
      </Stack>
    </TableCell>
  </TableRow>
);

const FindingCard: React.FC<{
  row: ShopifyMapAuditFinding;
  highlighted: boolean;
  resyncPending: boolean;
  resyncDisabled: boolean;
  onResync: () => void;
  onOpenProduct: () => void;
}> = ({ row, highlighted, resyncPending, resyncDisabled, onResync, onOpenProduct }) => (
  <Box
    sx={{
      p: 2,
      bgcolor: highlighted ? (theme) => alpha(theme.palette.warning.main, 0.08) : undefined,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2">{row.productName || row.productId}</Typography>
      <Typography variant="caption" color="text.secondary">
        {[row.productBrand, row.productSku || row.shopifySku].filter(Boolean).join(' · ')}
        {row.hostProductId ? ' · sibling variant on another Motzu SKU\'s listing' : ''}
      </Typography>
    </Box>
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
      {row.issues.map((issue) => (
        <StatusChip key={issue} size="small" label={ISSUE_LABEL[issue]} tone={ISSUE_TONE[issue]} />
      ))}
    </Stack>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 1.5 }}>
      <Box>
        <Typography component="span" sx={mobileLabelSx}>Shopify price</Typography>
        <Typography variant="body2">{money(row.livePrice)}</Typography>
      </Box>
      <Box>
        <Typography component="span" sx={mobileLabelSx}>Motzu price</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{money(row.intendedPrice)}</Typography>
      </Box>
      <Box>
        <Typography component="span" sx={mobileLabelSx}>MAP floor</Typography>
        <Typography variant="body2">{money(row.mapFloor)}</Typography>
      </Box>
      <Box>
        <Typography component="span" sx={mobileLabelSx}>Compare-at</Typography>
        <Typography variant="body2">{money(row.liveCompareAtPrice)}</Typography>
        <Typography variant="caption" color="text.secondary">Motzu {money(row.intendedCompareAtPrice)}</Typography>
      </Box>
    </Box>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
      {isAuditFindingResyncable(row.issues) && (row.intendedPrice != null || row.intendedCompareAtPrice != null) && (
        <Button
          size="small"
          variant="outlined"
          onClick={onResync}
          disabled={resyncDisabled}
          startIcon={resyncPending ? <CircularProgress size={12} /> : <CloudSync sx={{ fontSize: 14 }} />}
          sx={{ textTransform: 'none' }}
        >
          Resync
        </Button>
      )}
      <Button size="small" variant="outlined" onClick={onOpenProduct} sx={{ textTransform: 'none' }}>
        Motzu
      </Button>
      {row.shopifyAdminUrl && (
        <Button
          size="small"
          variant="outlined"
          href={row.shopifyAdminUrl}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
          sx={{ textTransform: 'none' }}
        >
          Shopify
        </Button>
      )}
    </Stack>
  </Box>
);

export default StoreShopifyPricingAuditPage;
